import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Wallet } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LIABILITY_TYPES = [
  'mortgage',
  'rent',
  'personal_loan',
  'credit_card',
  'asset_finance',
  'lease',
  'business_loan',
  'line_of_credit',
  'ato_debt',
  'tax_payment_plan',
  'other',
];

const emptyLiability = {
  liability_type: 'other',
  lender: '',
  original_amount: '',
  outstanding_balance: '',
  amount_owing: '',
  monthly_repayment: '',
  payment_frequency: '',
  payout_on_settlement: '',
  interest_rate: '',
  credit_limit: '',
  monthly_payment_three_percent: '',
  is_secured: '',
  is_business: '',
  to_be_refinanced: false,
  notes: '',
};

export default function LiabilitiesTab({ deal, dealId, onRefresh }) {
  const [liabilities, setLiabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingFlags, setSavingFlags] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyLiability);
  const [liabilityFlags, setLiabilityFlags] = useState({
    has_outstanding_loans: '',
    has_credit_cards: '',
  });

  const loadLiabilities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crm/deals/${dealId}/liabilities`);
      setLiabilities(res.data || []);
    } catch (error) {
      toast.error('Failed to load liabilities');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    loadLiabilities();
  }, [loadLiabilities]);

  useEffect(() => {
    setLiabilityFlags({
      has_outstanding_loans:
        deal?.has_outstanding_loans === true
          ? 'true'
          : deal?.has_outstanding_loans === false
            ? 'false'
            : '',
      has_credit_cards:
        deal?.has_credit_cards === true
          ? 'true'
          : deal?.has_credit_cards === false
            ? 'false'
            : '',
    });
  }, [deal]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyLiability);
    setDialogOpen(true);
  };

  const openEdit = (liability) => {
    setEditingId(liability.id);
    setForm({
      liability_type: liability.liability_type || 'other',
      lender: liability.lender || '',
      original_amount: liability.original_amount ?? '',
      outstanding_balance: liability.outstanding_balance ?? '',
      amount_owing: liability.amount_owing ?? '',
      monthly_repayment: liability.monthly_repayment ?? '',
      payment_frequency: liability.payment_frequency ?? '',
      payout_on_settlement: liability.payout_on_settlement === true ? 'true' : liability.payout_on_settlement === false ? 'false' : '',
      interest_rate: liability.interest_rate ?? '',
      credit_limit: liability.credit_limit ?? '',
      monthly_payment_three_percent: liability.monthly_payment_three_percent ?? '',
      is_secured: liability.is_secured === true ? 'true' : liability.is_secured === false ? 'false' : '',
      is_business: liability.is_business === true ? 'true' : liability.is_business === false ? 'false' : '',
      to_be_refinanced: !!liability.to_be_refinanced,
      notes: liability.notes || '',
    });
    setDialogOpen(true);
  };

  const normalizePayload = () => ({
    liability_type: form.liability_type || 'other',
    lender: form.lender || null,
    original_amount: form.original_amount === '' ? null : Number(form.original_amount),
    outstanding_balance: form.outstanding_balance === '' ? null : Number(form.outstanding_balance),
    amount_owing: form.amount_owing === '' ? null : Number(form.amount_owing),
    monthly_repayment: form.monthly_repayment === '' ? null : Number(form.monthly_repayment),
    payment_frequency: form.payment_frequency || null,
    payout_on_settlement: form.payout_on_settlement === '' ? null : form.payout_on_settlement === 'true',
    interest_rate: form.interest_rate === '' ? null : Number(form.interest_rate),
    credit_limit: form.credit_limit === '' ? null : Number(form.credit_limit),
    monthly_payment_three_percent: form.monthly_payment_three_percent === '' ? null : Number(form.monthly_payment_three_percent),
    is_secured: form.is_secured === '' ? null : form.is_secured === 'true',
    is_business: form.is_business === '' ? null : form.is_business === 'true',
    to_be_refinanced: !!form.to_be_refinanced,
    notes: form.notes || null,
  });

  const handleSaveFlags = async () => {
    setSavingFlags(true);
    try {
      await axios.put(`${API}/crm/deals/${dealId}`, {
        has_outstanding_loans: liabilityFlags.has_outstanding_loans === '' ? null : liabilityFlags.has_outstanding_loans === 'true',
        has_credit_cards: liabilityFlags.has_credit_cards === '' ? null : liabilityFlags.has_credit_cards === 'true',
      });
      toast.success('Liability declaration flags saved');
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to save declaration flags');
    } finally {
      setSavingFlags(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = normalizePayload();
      if (editingId) {
        await axios.put(`${API}/crm/liabilities/${editingId}`, payload);
        toast.success('Liability updated');
      } else {
        await axios.post(`${API}/crm/deals/${dealId}/liabilities`, payload);
        toast.success('Liability added');
      }
      setDialogOpen(false);
      await loadLiabilities();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to save liability');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (liabilityId) => {
    if (!window.confirm('Delete this liability?')) return;
    try {
      await axios.delete(`${API}/crm/liabilities/${liabilityId}`);
      toast.success('Liability deleted');
      await loadLiabilities();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to delete liability');
    }
  };

  if (loading) {
    return <div className="text-gray-400 text-sm" data-testid="liabilities-tab-loading">Loading liabilities...</div>;
  }

  return (
    <div className="max-w-6xl space-y-4" data-testid="liabilities-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-white font-medium flex items-center gap-2" data-testid="liabilities-tab-title">
          <Wallet className="h-5 w-5 text-rose-400" /> Liabilities
        </h2>
        <Button onClick={openCreate} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-liability-button">
          <Plus className="h-4 w-4 mr-1" /> Add Liability
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 bg-navy-800/40 border border-white/10 rounded-xl p-3" data-testid="liability-flags-panel">
        <Field label="Do you have any loans outstanding?">
          <select
            value={liabilityFlags.has_outstanding_loans}
            onChange={(e) => setLiabilityFlags((prev) => ({ ...prev, has_outstanding_loans: e.target.value }))}
            className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
            data-testid="liability-flag-outstanding-loans"
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </Field>
        <Field label="Do you have any credit cards?">
          <select
            value={liabilityFlags.has_credit_cards}
            onChange={(e) => setLiabilityFlags((prev) => ({ ...prev, has_credit_cards: e.target.value }))}
            className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
            data-testid="liability-flag-credit-cards"
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </Field>
        <div className="flex items-end">
          <Button
            onClick={handleSaveFlags}
            disabled={savingFlags}
            className="w-full md:w-auto bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="save-liability-flags-button"
          >
            {savingFlags ? 'Saving...' : 'Save Flags'}
          </Button>
        </div>
      </div>

      {liabilities.length === 0 ? (
        <div className="text-center text-gray-500 text-sm py-10 border border-white/10 rounded-xl bg-navy-800/30" data-testid="liabilities-empty-state">
          No liabilities captured yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-white/10 rounded-xl" data-testid="liabilities-table-wrapper">
          <table className="w-full min-w-[980px] text-sm" data-testid="liabilities-table">
            <thead className="bg-navy-900/80 text-xs uppercase text-gray-400">
              <tr>
                <th className="px-3 py-2 text-left">Liability Type</th>
                <th className="px-3 py-2 text-left">Lender</th>
                <th className="px-3 py-2 text-right">Amount Owing</th>
                <th className="px-3 py-2 text-right">Payment</th>
                <th className="px-3 py-2 text-left">Frequency</th>
                <th className="px-3 py-2 text-left">Payout?</th>
                <th className="px-3 py-2 text-left">To Be Refinanced</th>
                <th className="px-3 py-2 text-left">Notes</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {liabilities.map(liability => (
                <tr key={liability.id} className="border-t border-white/5 text-gray-200" data-testid={`liability-row-${liability.id}`}>
                  <td className="px-3 py-2 capitalize">{(liability.liability_type || 'other').replace(/_/g, ' ')}</td>
                  <td className="px-3 py-2">{liability.lender || '—'}</td>
                  <td className="px-3 py-2 text-right">{money(liability.amount_owing ?? liability.outstanding_balance)}</td>
                  <td className="px-3 py-2 text-right">{money(liability.monthly_repayment ?? liability.monthly_payment_three_percent)}</td>
                  <td className="px-3 py-2">{liability.payment_frequency || '—'}</td>
                  <td className="px-3 py-2">{liability.payout_on_settlement === true ? 'Yes' : liability.payout_on_settlement === false ? 'No' : '—'}</td>
                  <td className="px-3 py-2">{liability.to_be_refinanced ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 max-w-[220px] truncate" title={liability.notes || ''}>{liability.notes || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => openEdit(liability)}
                        className="p-1.5 rounded hover:bg-white/10 text-gray-300 hover:text-white"
                        data-testid={`edit-liability-${liability.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(liability.id)}
                        className="p-1.5 rounded hover:bg-red-500/10 text-gray-400 hover:text-red-400"
                        data-testid={`delete-liability-${liability.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl bg-navy-900 border border-white/10 text-white" data-testid="liability-editor-dialog">
          <DialogHeader>
            <DialogTitle data-testid="liability-editor-title">{editingId ? 'Edit Liability' : 'Add Liability'}</DialogTitle>
            <DialogDescription className="text-gray-400" data-testid="liability-editor-description">
              Capture all required fields for serviceability and refinance analysis.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Liability Type">
              <select
                value={form.liability_type}
                onChange={e => setForm(prev => ({ ...prev, liability_type: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-liability-type"
              >
                {LIABILITY_TYPES.map(type => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </Field>

            <Field label="Lender">
              <input
                value={form.lender}
                onChange={e => setForm(prev => ({ ...prev, lender: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-lender"
              />
            </Field>

            <Field label="Original Amount ($)">
              <input
                type="number"
                value={form.original_amount}
                onChange={e => setForm(prev => ({ ...prev, original_amount: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-original-amount"
              />
            </Field>

            <Field label="Outstanding Balance ($)">
              <input
                type="number"
                value={form.outstanding_balance}
                onChange={e => setForm(prev => ({ ...prev, outstanding_balance: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-outstanding-balance"
              />
            </Field>

            <Field label="Amount Owing ($)">
              <input
                type="number"
                value={form.amount_owing}
                onChange={e => setForm(prev => ({ ...prev, amount_owing: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-amount-owing"
              />
            </Field>

            <Field label="Monthly Repayment ($)">
              <input
                type="number"
                value={form.monthly_repayment}
                onChange={e => setForm(prev => ({ ...prev, monthly_repayment: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-monthly-repayment"
              />
            </Field>

            <Field label="Payment Frequency">
              <select
                value={form.payment_frequency}
                onChange={e => setForm(prev => ({ ...prev, payment_frequency: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-payment-frequency"
              >
                <option value="">Select...</option>
                <option value="Weekly">Weekly</option>
                <option value="Fortnightly">Fortnightly</option>
                <option value="Monthly">Monthly</option>
              </select>
            </Field>

            <Field label="Payout on Settlement?">
              <select
                value={form.payout_on_settlement}
                onChange={e => setForm(prev => ({ ...prev, payout_on_settlement: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-payout"
              >
                <option value="">Select...</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </Field>

            <Field label="Interest Rate (%)">
              <input
                type="number"
                value={form.interest_rate}
                onChange={e => setForm(prev => ({ ...prev, interest_rate: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-interest-rate"
              />
            </Field>

            <Field label="Credit Limit ($)">
              <input
                type="number"
                value={form.credit_limit}
                onChange={e => setForm(prev => ({ ...prev, credit_limit: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-credit-limit"
              />
            </Field>

            <Field label="Monthly Payment (3%) ($)">
              <input
                type="number"
                value={form.monthly_payment_three_percent}
                onChange={e => setForm(prev => ({ ...prev, monthly_payment_three_percent: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-monthly-payment-three-percent"
              />
            </Field>

            <Field label="Secured / Unsecured">
              <select
                value={form.is_secured}
                onChange={e => setForm(prev => ({ ...prev, is_secured: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-secured"
              >
                <option value="">Select...</option>
                <option value="true">Secured</option>
                <option value="false">Unsecured</option>
              </select>
            </Field>

            <Field label="Business / Personal">
              <select
                value={form.is_business}
                onChange={e => setForm(prev => ({ ...prev, is_business: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-business-personal"
              >
                <option value="">Select...</option>
                <option value="true">Business</option>
                <option value="false">Personal</option>
              </select>
            </Field>

            <Field label="To Be Refinanced" className="md:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-300" data-testid="liability-form-refinanced-toggle">
                <input
                  type="checkbox"
                  checked={!!form.to_be_refinanced}
                  onChange={e => setForm(prev => ({ ...prev, to_be_refinanced: e.target.checked }))}
                  className="rounded border-white/20 bg-navy-800 text-purple-500"
                />
                Mark as to be refinanced
              </label>
            </Field>

            <Field label="Notes" className="md:col-span-2">
              <textarea
                rows={3}
                value={form.notes}
                onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="liability-form-notes"
              />
            </Field>
          </div>

          <DialogFooter>
            <Button onClick={() => setDialogOpen(false)} variant="outline" className="border-white/10 text-gray-300" data-testid="cancel-liability-button">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-liability-button">
              {saving ? 'Saving...' : editingId ? 'Update Liability' : 'Add Liability'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, className = '', children }) {
  return (
    <div className={className}>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

function money(value) {
  if (value === null || value === undefined || value === '') return '—';
  return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}